import { Box, Hide, Stack } from '@chakra-ui/react';
import { Grid } from '@time-line/components/common';
import { PromoCode } from 'components/common/PromoCode';
import {
  ProductBulletPoints,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { ProductBundles } from 'components/product/ProductBundles';
import { ProductCheckmarks } from 'components/product/ProductCheckmarks';
import { ProductConfiguratorRender } from 'components/product/ProductConfiguratorRender';
import { ProductDisclaimer } from 'components/product/ProductDisclaimer';
import { ProductGalleryDesktop } from 'components/product/ProductGallery/ProductGalleryDesktop';
import { ProductGalleryMobile } from 'components/product/ProductGallery/ProductGalleryMobile';
import { ProductIntro } from 'components/product/ProductIntro';
import { useProductCollection } from 'hooks/product/useProductCollection';
import { useProductsUpdatedPrices } from 'hooks/product/useProductsUpdatedPrices';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import type { ProductBlock as ProductBlockType } from 'types/cms/blocks';
import { resolveProductInfo } from 'utils/product/resolveProductInfo';

export function ProductBlock({
  _key,
  productWithVariants,
  productOverrides,
  promoCodes,
  isPromoCodeVisible,
  isDiscountHidden,
  ctaType,
  purchaseType,
  amazonButton,
}: ProductBlockType) {
  const { product, variants: selectedVariants } = productWithVariants;
  const {
    slug,
    mostPopularVariantId,
    bestValueVariantId,
    media /* First 2 from PDP */,
    bulletPoints,
    bundleProducts,
    disclaimer,
  } = product;

  const { data: shopifyProduct, isLoading: isProductLoading } =
    useShopifyProduct(slug);
  const sanityProduct = useSanityProduct(slug, { required: true });

  const bundleCollection = bundleProducts
    ? { products: bundleProducts.map((item) => item.product) }
    : undefined;
  const bundle = useProductCollection(bundleCollection);
  const bundleFreeBySlug = new Map(
    bundleProducts?.map((item) => [item.product.slug, item.isFree]),
  );
  const bundleVariantGidBySlug = new Map(
    bundleProducts?.map((item) => [item.product.slug, item.variant?.gid]),
  );
  const bundleItems = bundle.map((bundleProduct) => ({
    product: bundleProduct,
    isFree: bundleFreeBySlug.get(bundleProduct.slug) ?? false,
    variantGid: bundleVariantGidBySlug.get(bundleProduct.slug),
  }));

  const selectedVariantsIds = selectedVariants?.map((item) => item.gid) || [];
  const { variants, availableForSale } = shopifyProduct || {};

  const productInfo = resolveProductInfo({
    sanityProduct,
    productOverrides,
    variants,
    selectedVariantsIds,
    images: media,
  });

  const { data: updatedProducts } = useProductsUpdatedPrices({
    blockId: _key,
    shopifyProducts: shopifyProduct ? [shopifyProduct] : null,
    promoCodes,
  });

  const updatedProduct = updatedProducts?.[0];

  return (
    <Grid id={_key} className="section--product" my="2xl">
      {productInfo.images.length > 0 && (
        <Hide above="lg">
          <Box
            className="product_media"
            gridColumn="span 12"
            display={{ base: 'block', lg: 'none' }}
          >
            <ProductGalleryMobile
              media={productInfo.images}
              title={productInfo.title}
              isDoctorRecommended={sanityProduct.isDoctorRecommended}
              isLoading={isProductLoading}
            />
          </Box>
        </Hide>
      )}
      <Grid.Inner gridRowGap={{ base: 'm', md: 'lg' }}>
        {productInfo.images.length > 0 && (
          <Hide below="lg">
            <Box
              className="product_media"
              gridColumn="span 8"
              mr="5%"
              position="sticky"
              top="2xl"
              alignSelf="start"
              display={{ base: 'none', lg: 'block' }}
            >
              <ProductGalleryDesktop
                media={productInfo.images}
                title={productInfo.title}
                isDoctorRecommended={sanityProduct.isDoctorRecommended}
                isLoading={isProductLoading}
              />
            </Box>
          </Hide>
        )}
        <Stack
          className="product_content"
          rowGap="sm"
          gridColumn={{ base: '1/-1', lg: '9/-1' }}
          gridRow={{ base: 2, lg: 1 }}
        >
          <ProductIntro
            title={productInfo.title}
            subtitle={productInfo.subtitle}
            longDescription={productInfo.description}
            productBullet={sanityProduct.productBullet}
            secondaryProductBullet={sanityProduct.secondaryProductBullet}
            isMoneyBackGuarantee={sanityProduct.isMoneyBackGuarantee}
            showReviews
            productSlugs={[slug]}
          />
          {bulletPoints?.length && (
            <Box>
              <RichTextResolver
                blocks={bulletPoints}
                container={ProductBulletPoints}
              />
            </Box>
          )}
          {!!bundleItems.length && <ProductBundles items={bundleItems} />}
          {isPromoCodeVisible &&
            availableForSale &&
            promoCodes &&
            promoCodes?.length > 0 && (
              <PromoCode title={productInfo.title} promoCode={promoCodes[0]} />
            )}
          <Box className="product_configurator">
            <ProductConfiguratorRender
              slug={slug}
              shopifyProduct={updatedProduct}
              isLoading={isProductLoading}
              isPreorder={sanityProduct.isPreorder}
              mostPopularVariantId={mostPopularVariantId}
              bestValueVariantId={bestValueVariantId}
              selectedVariantsIds={selectedVariantsIds}
              promoCodes={promoCodes}
              ctaType={ctaType}
              amazonButton={amazonButton}
              asin={sanityProduct.asin}
              purchaseType={purchaseType}
              isDiscountHidden={isDiscountHidden}
            />
          </Box>
          <ProductCheckmarks
            productId={shopifyProduct?.id}
            isShippingInfoVisible
            isTruemedVisible={false}
          />
          {disclaimer && <ProductDisclaimer>{disclaimer}</ProductDisclaimer>}
        </Stack>
      </Grid.Inner>
    </Grid>
  );
}
